/**
 * S3 multipart upload for raw source files too large for one PUT (GDN-4132).
 *
 * S3 caps a single PUT at 5 GB, and a customer's archive can be 50 GB. A file
 * over the threshold is sliced with `Blob.slice` (no bytes are read until a
 * part is sent), its parts are PUT several at a time to presigned part URLs,
 * each part's `ETag` is kept, and the upload is completed with them.
 *
 * The server calls sit behind {@link MultipartAdapter} and the part PUT behind
 * {@link PutPart}, so this module is pure orchestration: the adapter is wired
 * to the generated admin routes, and tests drive both seams directly.
 */
import { readLaunchRefusal } from './launch-refusal';
import { readPresignBlockReason, S3UploadError, s3ErrorFromResponse } from './raw-upload-outcome';

/** Files at or above this size upload through multipart. */
export const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024;

/** S3's floor for every part but the last. */
const MIN_PART_BYTES = 5 * 1024 * 1024;
/** S3's ceiling on parts per upload. */
const MAX_PARTS = 10_000;
/** The part size used when it fits; grown for files too big for it. */
const DEFAULT_PART_BYTES = 64 * 1024 * 1024;

/** How many parts are in flight at once. */
const DEFAULT_CONCURRENCY = 4;
/**
 * How many times a part is retried after its first failure. Each retry asks
 * for a fresh URL (the old one may have expired while the part waited) and
 * waits first, with backoff: a Wi-Fi reconnect fails a PUT at once, and
 * retries spent in the same second would discard hours of a large upload.
 */
const DEFAULT_PART_RETRIES = 5;
/** How many times a `complete` that failed without a definite refusal is retried. */
const COMPLETE_RETRIES = 4;

/** Exponential backoff with jitter: about 1, 2, 4, 8, 16 s, capped at 30 s. */
export function backoffDelay(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, 30_000);
  return base / 2 + Math.random() * (base / 2);
}
/**
 * How many part URLs to presign per request. Presigned URLs expire, so parts
 * are presigned shortly before they are sent, never all up front.
 */
const PRESIGN_BATCH = 16;

export function shouldUseMultipart(size: number): boolean {
  return size >= MULTIPART_THRESHOLD_BYTES;
}

/**
 * The part size for a file: the default when it fits in S3's part limit,
 * otherwise the smallest whole MiB that does. Never under S3's 5 MiB floor.
 */
export function choosePartSize(size: number, preferred = DEFAULT_PART_BYTES): number {
  const mib = 1024 * 1024;
  const needed = Math.ceil(size / MAX_PARTS / mib) * mib;
  return Math.max(preferred, needed, MIN_PART_BYTES);
}

export interface MultipartSession {
  /** The raw upload (batch) the file belongs to; start mints one when none is given. */
  batchId: string;
  /** The object key the parts assemble into. */
  key: string;
  /** S3's multipart upload id. */
  multipartUploadId: string;
  /** The highest part number the server will presign, when it says. */
  maxPartNumber?: number;
}

/** Which batch a start joins, as the raw-upload surface expects it. */
export interface MultipartBatch {
  /** Omitted to mint a new batch. */
  uploadId?: string;
  /** Joining a batch this run already opened: the server skips enumerating it. */
  continuation: boolean;
}

export interface PresignedPart {
  partNumber: number;
  url: string;
  /** Headers the PUT must send. None today: SSE-KMS is set when the upload starts. */
  headers?: Record<string, string>;
}

export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** The server's multipart calls, as the raw-upload surface exposes them. */
export interface MultipartAdapter {
  start(
    file: { filename: string; size: number; partCount: number },
    batch: MultipartBatch,
  ): Promise<MultipartSession>;
  presignParts(session: MultipartSession, partNumbers: number[]): Promise<PresignedPart[]>;
  complete(session: MultipartSession, parts: CompletedPart[]): Promise<void>;
  abort(session: MultipartSession): Promise<void>;
}

/**
 * PUT one part's bytes and resolve with the `ETag` S3 returned. Rejects with
 * an {@link S3UploadError} on a refusal or when there is no response.
 */
export type PutPart = (
  part: PresignedPart,
  body: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
) => Promise<string>;

/** The run was cancelled. Distinct from a failure: nothing needs explaining. */
export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadCancelledError';
  }
}

/**
 * A part failure that no retry can cure (an unreadable ETag is the bucket's
 * CORS, not the network). Retrying would resend the part for nothing.
 */
export class PermanentPartError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermanentPartError';
  }
}

/**
 * S3 may have assembled the file, but the reply to `complete` never arrived.
 * The upload is left open rather than aborted (an abort would not remove a
 * finished object, and would destroy the parts if it had not finished); the
 * bucket's lifecycle rule clears it if it never completed.
 */
export class CompletionUnconfirmedError extends Error {
  constructor() {
    super(
      'All parts were sent, but the server did not confirm the file was assembled. Refresh Imports to check whether it arrived before uploading it again.',
    );
    this.name = 'CompletionUnconfirmedError';
  }
}

/** Wait, unless cancelled first. */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new UploadCancelledError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export interface MultipartUploadOptions {
  file: Blob;
  filename: string;
  batch: MultipartBatch;
  /** Called once the upload is open, before any part is sent. */
  onStarted?: (session: MultipartSession) => void;
  adapter: MultipartAdapter;
  putPart: PutPart;
  /** Bytes of this file that have reached S3 so far (in-flight parts included). */
  onProgress?: (loaded: number) => void;
  signal?: AbortSignal;
  partSize?: number;
  concurrency?: number;
  partRetries?: number;
  /** The wait before retry `attempt` (0-based). Backoff with jitter by default. */
  retryDelay?: (attempt: number) => number;
}

/**
 * Upload one file through multipart. Resolves once S3 has assembled the
 * object. On a part that keeps failing, or on cancel, the upload is aborted on
 * the server (so no parts are left billing) and the error is rethrown: the
 * part's {@link S3UploadError}, or {@link UploadCancelledError}.
 */
export async function uploadMultipart({
  file,
  filename,
  batch,
  onStarted,
  adapter,
  putPart,
  onProgress,
  signal,
  partSize = choosePartSize(file.size),
  concurrency = DEFAULT_CONCURRENCY,
  partRetries = DEFAULT_PART_RETRIES,
  retryDelay = backoffDelay,
}: MultipartUploadOptions): Promise<MultipartSession> {
  const partCount = Math.max(1, Math.ceil(file.size / partSize));
  if (partCount > MAX_PARTS) {
    throw new Error(
      `File needs ${partCount} parts; S3 allows ${MAX_PARTS}. Use a larger part size.`,
    );
  }
  if (signal?.aborted) throw new UploadCancelledError();

  const session = await adapter.start({ filename, size: file.size, partCount }, batch);

  // Everything after start owns an open upload on the server: any exit other
  // than completion aborts it, unless completion itself is in doubt.
  const internal = new AbortController();
  const onOuterAbort = () => internal.abort();
  signal?.addEventListener('abort', onOuterAbort);
  // A cancel pressed while start was in flight has already fired; a listener
  // added now would never hear it.
  if (signal?.aborted) internal.abort();

  // Kept as a running total: a 50 GB file reports progress for hours.
  const loadedByPart = new Map<number, number>();
  let loadedTotal = 0;
  const setLoaded = (partNumber: number, loaded: number) => {
    loadedTotal += loaded - (loadedByPart.get(partNumber) ?? 0);
    loadedByPart.set(partNumber, loaded);
    onProgress?.(loadedTotal);
  };

  // Presign lazily in batches, so no URL waits long enough to expire. A part
  // whose batch already went by (answered without it, or failed) gets its own
  // single-part request instead of pulling the next batch forward.
  const urls = new Map<number, PresignedPart>();
  let presigned = 0;
  let presigning: Promise<void> | null = null;
  const freshUrlFor = async (partNumber: number): Promise<PresignedPart> => {
    const [part] = await adapter.presignParts(session, [partNumber]);
    if (!part || part.partNumber !== partNumber) {
      throw new Error(`The server returned no upload link for part ${partNumber}`);
    }
    return part;
  };
  const urlFor = async (partNumber: number): Promise<PresignedPart> => {
    for (;;) {
      const ready = urls.get(partNumber);
      if (ready) {
        // Used once: a retry must ask for a fresh one.
        urls.delete(partNumber);
        return ready;
      }
      if (presigning) {
        await presigning.catch(() => undefined);
        continue;
      }
      if (partNumber <= presigned) return freshUrlFor(partNumber);
      const first = presigned + 1;
      const numbers = Array.from(
        { length: Math.min(PRESIGN_BATCH, partCount - presigned) },
        (_, i) => first + i,
      );
      presigned += numbers.length;
      presigning = adapter
        .presignParts(session, numbers)
        .then(parts => {
          for (const part of parts) urls.set(part.partNumber, part);
        })
        .finally(() => {
          presigning = null;
        });
    }
  };

  const sendPart = async (partNumber: number): Promise<CompletedPart> => {
    const start = (partNumber - 1) * partSize;
    const body = file.slice(start, Math.min(start + partSize, file.size));
    for (let attempt = 0; ; attempt += 1) {
      if (internal.signal.aborted) throw new UploadCancelledError();
      try {
        const url = attempt === 0 ? await urlFor(partNumber) : await freshUrlFor(partNumber);
        const etag = await putPart(
          url,
          body,
          loaded => setLoaded(partNumber, loaded),
          internal.signal,
        );
        setLoaded(partNumber, body.size);
        return { partNumber, etag };
      } catch (err) {
        // A failed attempt's bytes did not land.
        setLoaded(partNumber, 0);
        if (internal.signal.aborted) throw new UploadCancelledError();
        // The upload itself is gone (aborted, expired, replaced by a later
        // start): no retry of this part can land. The caller restarts the file.
        if (
          err instanceof PermanentPartError ||
          readPresignBlockReason(err) === 'multipart_upload_not_found' ||
          attempt >= partRetries
        ) {
          throw err;
        }
        await wait(retryDelay(attempt), internal.signal);
      }
    }
  };

  // Retried when the failure says nothing definite: no response, a 5xx, or
  // the server's transient status lookup. A retried complete is safe: one S3
  // already finished answers 200.
  const completeWithRetry = async (parts: CompletedPart[]): Promise<void> => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        await adapter.complete(session, parts);
        return;
      } catch (err) {
        const refusal = readLaunchRefusal(err);
        const transient =
          refusal.unconfirmed ||
          (refusal.status ?? 0) >= 500 ||
          readPresignBlockReason(err) === 'conversion_status_unavailable';
        if (!transient) throw err;
        if (attempt >= COMPLETE_RETRIES) {
          // Never answered definitively (no response, or only server errors):
          // S3 may hold the whole file, so it is neither failed nor aborted.
          throw new CompletionUnconfirmedError();
        }
        await wait(retryDelay(attempt), internal.signal);
      }
    }
  };

  const completed: CompletedPart[] = [];
  try {
    onStarted?.(session);
    if (session.maxPartNumber !== undefined && partCount > session.maxPartNumber) {
      throw new Error(`File needs ${partCount} parts; the server allows ${session.maxPartNumber}.`);
    }

    let next = 1;
    let failure: unknown;
    const workers = Array.from({ length: Math.min(concurrency, partCount) }, async () => {
      while (next <= partCount && failure === undefined) {
        const partNumber = next;
        next += 1;
        try {
          completed.push(await sendPart(partNumber));
        } catch (err) {
          // First failure wins; stop the other parts rather than finish a
          // file that can no longer complete.
          if (failure === undefined) failure = err;
          internal.abort();
        }
      }
    });
    await Promise.all(workers);
    if (signal?.aborted) throw new UploadCancelledError();
    if (failure !== undefined) throw failure;

    completed.sort((a, b) => a.partNumber - b.partNumber);
    await completeWithRetry(completed);
    return session;
  } catch (err) {
    if (!(err instanceof CompletionUnconfirmedError)) {
      // Free the parts now; the bucket's lifecycle rule is only the backstop.
      // An abort that fails must not hide the reason the upload stopped.
      await adapter.abort(session).catch(() => undefined);
    }
    throw signal?.aborted ? new UploadCancelledError() : err;
  } finally {
    signal?.removeEventListener('abort', onOuterAbort);
  }
}

/**
 * The browser implementation of {@link PutPart}: an XHR PUT, so upload
 * progress is real, that reads the `ETag` response header. The bucket's CORS
 * exposes `ETag`; if it ever stops, the part cannot be completed, and that is
 * said plainly rather than sent to S3 as an empty tag.
 */
export const xhrPutPart: PutPart = (part, body, onProgress, signal) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', part.url, true);
    for (const [name, value] of Object.entries(part.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(s3ErrorFromResponse(xhr.status, xhr.responseText));
        return;
      }
      const etag = xhr.getResponseHeader('ETag');
      if (!etag) {
        reject(
          new PermanentPartError(
            'S3 accepted a part but its ETag was not readable, so the file cannot be completed. The bucket CORS must expose ETag.',
          ),
        );
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () => reject(new S3UploadError(0));
    xhr.onabort = () => reject(new UploadCancelledError());
    if (signal.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    // Released when the part settles: a 50 GB file sends hundreds of parts,
    // and each listener would otherwise keep its request alive until the end.
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    xhr.addEventListener('loadend', () => signal.removeEventListener('abort', onAbort));
    xhr.send(body);
  });
